'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, LogIn, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';


interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

interface DemoCredential {
  role: string;
  email: string;
  password: string;
  color: string;
}

const demoCredentials: DemoCredential[] = [
  { role: 'Gestor',       email: 'rafael.costa@campus.edu.br',     password: 'Gestor@2026',    color: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'   },
  { role: 'Técnico',      email: 'marcos.peixoto@campus.edu.br',   password: 'Tecnico@2026',   color: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
  { role: 'Solicitante',  email: 'prof.lima@campus.edu.br',        password: 'Solicit@2026',   color: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200'   },
  { role: 'Diretor',      email: 'diretor.geral@campus.edu.br',    password: 'Diretor@2026',   color: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'      },
];

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', remember: false },
  });

  // Backend integration point: replace with POST /api/auth/login
  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setIsLoading(false);

    const valid = demoCredentials.find(
      (c) => c.email === data.email && c.password === data.password
    );

    if (valid) {
      toast.success(`Bem-vindo de volta! Entrando como ${valid.role}...`);
      setTimeout(() => { window.location.href = '/'; }, 800);
    } else {
      toast.error('Credenciais inválidas — use as contas demo abaixo para entrar.');
    }
  };

  const handleAutofill = (cred: DemoCredential) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
    toast.success(`Credenciais de ${cred.role} preenchidas.`);
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-700 text-foreground">Bem-vindo de volta</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Entre com suas credenciais institucionais
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Email */}
        <div>
          <label htmlFor="login-email" className="block text-sm font-600 text-foreground mb-1.5">
            E-mail institucional
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="seu.nome@campus.edu.br"
            {...register('email', {
              required: 'E-mail é obrigatório.',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Informe um e-mail válido.',
              },
            })}
            className={`w-full px-3.5 py-2.5 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring placeholder:text-muted-foreground transition-colors duration-150 ${
              errors.email ? 'border-red-400 bg-red-50/30' : 'border-border'
            }`}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-600 font-500">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="block text-sm font-600 text-foreground">
              Senha
            </label>
            <button type="button" className="text-xs text-primary hover:underline font-500">
              Esqueci a senha
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Sua senha"
              {...register('password', {
                required: 'Senha é obrigatória.',
                minLength: { value: 6, message: 'Mínimo 6 caracteres.' },})}
              className={`w-full px-3.5 py-2.5 pr-10 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring placeholder:text-muted-foreground transition-colors duration-150 ${
                errors.password ? 'border-red-400 bg-red-50/30' : 'border-border'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-600 font-500">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            id="login-remember"
            type="checkbox"
            {...register('remember')}
            className="w-4 h-4 rounded border-border text-primary focus:ring-ring/30 cursor-pointer"
          />
          <label htmlFor="login-remember" className="text-sm text-muted-foreground cursor-pointer select-none">
            Manter sessão ativa
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground text-sm font-700 py-2.5 rounded-lg hover:bg-primary/90 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 shadow-card mt-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Entrando...
            </>
          ) : (
            <>
              <LogIn size={16} />
              Entrar no sistema
            </>
          )}
        </button>
      </form>

      {/* Demo credentials */}
      <div className="mt-6 border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-secondary/40 border-b border-border">
          <p className="text-xs font-600 text-foreground">Contas de demonstração</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Clique em um perfil para preencher automaticamente
          </p>
        </div>
        <div className="divide-y divide-border">
          {demoCredentials.map((cred) => (
            <div
              key={`demo-${cred.role}`}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/30 transition-colors duration-150"
            >
              <button
                type="button"
                onClick={() => handleAutofill(cred)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
              >
                <span className={`status-badge shrink-0 ${cred.color}`}>{cred.role}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-500 text-foreground truncate">{cred.email}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">{cred.password}</p>
                </div>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => handleCopy(cred.email, `${cred.role}-email`)}
                  title="Copiar e-mail"
                  className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors duration-150"
                >
                  {copiedField === `${cred.role}-email` ? (
                    <Check size={12} className="text-emerald-600" />
                  ) : (
                    <Copy size={12} />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}